/**
 * How much of the game's large files (atlases, models, textures) this browser keeps (store.ts),
 * and a button that clears everything the game has cached (register.ts).
 */
import { settings } from '../settings';
import { defineSetting } from '../settingsRegistry';
import { megabytes } from './lru';
import { clearGameCache } from './register';
import { assetStore, setCacheCap } from './store';

declare module '../settingsRegistry' {
  interface Settings {
    /** The cap on files kept in IndexedDB: an index into `CACHE_CAPS`. */
    assetCache: number;
  }
}

/** Bytes kept at most, by choice (0: nothing kept, every load downloads). */
export const CACHE_CAPS = [0, 256e6, 512e6, 1e9, 2e9] as const;
const DEFAULT = 3;

defineSetting({
  key: 'assetCache',
  default: DEFAULT,
  order: 300,
  range: [0, CACHE_CAPS.length - 1],
  menu: {
    group: 'data',
    label: 'เก็บข้อมูลเกมไว้ในเครื่อง',
    choices: ['ไม่เก็บ', '256 MB', '512 MB', '1 GB', '2 GB'],
    changed: () => void assetStore.shrink(),
    note: () => {
      const used = assetStore.usage();
      if (used === null) {
        void assetStore.open();
        return null;
      }
      return `ใช้อยู่ ${megabytes(used)} MB`;
    },
    action: { label: 'ล้างข้อมูลเกมที่เก็บไว้', run: clearGameCache },
  },
});

// Read when a file is kept, never at import (settings.ts loads this file before the saved values).
setCacheCap(() => CACHE_CAPS[settings.assetCache] ?? CACHE_CAPS[DEFAULT]);
